import dayjs from 'dayjs';

import { getL10nDate } from '@travel/i18n/src';
import { getL10nArea } from '@travel/i18n/src/components/L10nArea';
import { Checkmark } from '@travel/icons/ui';
import type { GetTranslation } from '@travel/translation';
import { useTranslation } from '@travel/traveler-core/hooks';
import type { Facility } from '@travel/traveler-core/types/providerRatePlan';
import { isEmptyArray, isNotEmptyArray } from '@travel/utils';

import { DATE_FORM_FORMAT } from 'components/DateInput';

import { EMPTY_ARRAY } from 'constants/defaultValue';
import { PLAN_FEATURES } from 'constants/planFeatures';
import type { ProviderInformation } from 'ProviderInformation-Types';
import type { BedSizeItem, Capacity, Feature, RoomSizeItem } from 'ProviderRatePlan-Types';
import type { ProviderReviewList } from 'ProviderReview-Types';
import { roundReviewScore } from 'utils/review';

type IconText = {
  id: string;
  icon?: string;
  name?: string;
  text?: string;
  // detail
  title?: string;
  description?: string;
};

interface IconTextListData extends Omit<IconText, 'icon'> {
  groupId: undefined;
  icon: React.ReactNode;
}

interface GetBedDetailsOptions {
  beds: BedSizeItem[] | undefined;
  isIndirectProvider: boolean;
  shouldTranslate?: boolean;
  getTranslation: GetTranslation;
}

type CapacityCondition =
  | 'status == min is not equal to max'
  | 'status == min = max = 1 (Singular)'
  | 'status == min = max (plural)';

export function getCapacityText(capacity: Capacity, getTranslation: GetTranslation) {
  const { minGuests, maxGuests } = capacity;
  if (!minGuests || !maxGuests) {
    return '';
  }

  let capacityCondition: CapacityCondition;
  if (minGuests !== maxGuests) {
    capacityCondition = 'status == min is not equal to max';
  } else if (maxGuests === 1) {
    capacityCondition = 'status == min = max = 1 (Singular)';
  } else {
    capacityCondition = 'status == min = max (plural)';
  }

  return getTranslation({
    id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Capacity',
    data: {
      min_guests: minGuests,
      max_guests: maxGuests,
    },
    condition: () => capacityCondition,
  });
}

export function getBedDetailsTranslated(
  bed: BedSizeItem,
  getTranslation: GetTranslation,
  shouldTranslate: boolean,
): string {
  const { name, amount } = bed;
  if (!shouldTranslate) {
    return amount ? `${amount} x ${name}` : name;
  } else {
    if (amount) {
      return getTranslation({
        id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Bed',
        data: { bed_count: amount, bed_type: name },
        count: amount,
      });
    }
    return name;
  }
}

export function getBedDetails({
  beds,
  isIndirectProvider,
  shouldTranslate = true,
  getTranslation,
}: GetBedDetailsOptions): string {
  if (isEmptyArray(beds)) {
    return '';
  }

  const formatBedData = (bed: BedSizeItem) =>
    getBedDetailsTranslated(bed, getTranslation, isIndirectProvider ? true : shouldTranslate); // always translate for indirect providers

  if (!isIndirectProvider) {
    return beds.map(formatBedData).join(', ');
  }

  //for indirect providers, group all beds based on logicGroupNumber (https://confluence.rakuten-it.com/confluence/display/EVEREST/Bed+Type+Display+Logic+for+Indirect+Providers)
  const groupedBeds = beds.reduce((acc: Record<number, Array<BedSizeItem>>, bed) => {
    if (!bed.logicGroupNumber) {
      return acc;
    }

    if (!acc[bed.logicGroupNumber]) {
      acc[bed.logicGroupNumber] = [];
    }
    acc[bed.logicGroupNumber].push(bed);
    return acc;
  }, {});

  const andLabel = getTranslation({
    id: 'Common_Plan_Details.Out_To_Out.Room_Plan_Info.Bed_Type.And',
  });

  const orLabel = getTranslation({
    id: 'Common_Plan_Details.Out_To_Out.Room_Plan_Info.Bed_Type.Or',
  });

  const bedText = Object.keys(groupedBeds)
    .map(Number)
    .sort((a, b) => a - b)
    .map((key) => {
      const group = groupedBeds[key];
      return group.map((bed) => formatBedData(bed)).join(` ${andLabel} `);
    })
    .join(` ${orLabel} `);

  return bedText;
}

export function getRoomSizeText(room?: RoomSizeItem) {
  return room?.area && room?.unit ? getL10nArea(room.area, room.unit, 1) : null;
}

export function mappedIconTextListData(
  facilities: Array<IconText>,
  iconClassName?: string,
  iconPlaceHolder: React.ReactNode = <Checkmark size={16} />,
) {
  if (isEmptyArray(facilities)) {
    return EMPTY_ARRAY;
  }

  // to remove duplicated feature id
  const features: IconText[] = Object.values(
    (facilities || EMPTY_ARRAY).reduce(
      (result, facility) => ({ ...result, [facility.id]: facility }),
      {},
    ),
  );

  return isNotEmptyArray(features)
    ? features.reduce((result, { name, text, icon, ...rest }) => {
        const label = name || text;
        if (!label || label.trim().length === 0) {
          return result;
        }

        return [
          ...result,
          {
            text: label,
            icon: icon ? (
              <img src={icon} alt={name || text} className={iconClassName} loading={'lazy'} />
            ) : (
              iconPlaceHolder || null
            ),
            ...rest,
            groupId: undefined,
          },
        ];
      }, [] as IconTextListData[])
    : EMPTY_ARRAY;
}

export function getFilteredRoomPlanFeatures(
  facilities?: Array<Facility>,
  start?: number,
  end?: number,
) {
  return mappedIconTextListData(
    isNotEmptyArray(facilities)
      ? facilities
          .reduce(
            (featureList, currentFacility) => [...featureList, ...currentFacility.features],
            [] as Array<Feature>,
          )
          .filter(
            (feature) =>
              feature.id !== PLAN_FEATURES.NO_MEAL && feature.id !== PLAN_FEATURES.CREDIT_CARD_ONLY, // To avoid duplication with paymentRestrictionFeature & hasMeal
          )
          .slice(start, end)
      : EMPTY_ARRAY,
  );
}

/**
 * For use with analytics.
 * @param hasMeal If plan includes meal.
 * @param freeUntil Free cancellation deadline for plan if available.
 * @param facilities Plan facilities.
 * @param start Start index to filter feature facilities.
 * @param end End index to filter facilities.
 * @returns Array of feature text.
 */
export function getExtendedFilteredRoomPlanFeatures(
  hasMeal: boolean,
  freeUntil?: string,
  facilities?: Array<Facility>,
  start?: number,
  end?: number,
) {
  const features = [];
  if (freeUntil) {
    features.push(
      useTranslation({
        id: 'Cancellation_Policy_Common.Provider_Pages.Free_Cancellation',
        data: { free_cancellation_date: getL10nDate('', freeUntil, 'MD', true, '') },
      }),
    );
  }
  if (hasMeal) {
    features.push(useTranslation({ id: 'Provider_Info.Luxury.Meal' }));
  } else {
    features.push(useTranslation({ id: 'Common_Price_Details.Guests.No_Meal' }));
  }
  const filteredFeatures = getFilteredRoomPlanFeatures(facilities, start, end).flatMap((feature) =>
    feature.text ? [feature.text] : [],
  );
  return features.concat(filteredFeatures).slice(start, end);
}

export function generateProviderInfoJsonLd(
  provider: ProviderInformation,
  reviewScoreInfo: ProviderReviewList,
) {
  const {
    address: { postCode, addressLine } = {},
    description,
    contact: { phoneInfo = [] } = {},
    media = [],
  } = provider;
  const { averageScore, total } = reviewScoreInfo;

  return {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: provider?.name || '',
    address: {
      '@type': 'PostalAddress',
      postalCode: postCode,
      streetAddress: addressLine,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: total && averageScore ? roundReviewScore(averageScore) : '',
      ratingCount: total || '',
    },
    description: description || '',
    telephone: phoneInfo?.[0] || '',
    image: media[0]?.url || '',
  };
}

// Match the ids of the features in query to their values
export function createScondIds(mapping: { [p: string]: string }, features?: string): string {
  return Object.entries(mapping)
    .reduce((acc, [key, val]) => {
      if (features?.includes(val)) {
        return [...acc, key];
      }
      return acc;
    }, [] as Array<string>)
    .join(',');
}

export function isDateRangeValid(startDate: string, endDate: string): boolean {
  const today = dayjs();
  const oneYearFromTomorrow = today.add(366, 'day');

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  /* This code checks that the start date is today or later, the start date is before the end date,
     the end date is today or later, and the end date is within 365 days from tomorrow. */

  const isStartValid =
    start.isAfter(today) || start.format(DATE_FORM_FORMAT) === today.format(DATE_FORM_FORMAT);
  const isStartWithinOneYear = start.isBefore(oneYearFromTomorrow);
  const isEndValid = end.isAfter(today);
  const isEndWithinOneYear =
    end.isBefore(oneYearFromTomorrow) ||
    end.format(DATE_FORM_FORMAT) === oneYearFromTomorrow.format(DATE_FORM_FORMAT);

  return isStartValid && isStartWithinOneYear && isEndValid && isEndWithinOneYear;
}
